// src/App.js
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { Box, CircularProgress } from '@mui/material';

// Auth Components
import Login from './components/pages/auth/Login';
import Register from './components/pages/auth/Register';
import ForgotPassword from './components/pages/auth/ForgotPassword';

// Layout Components
import Dashboard from './components/pages/Dashboard';
import UserDashboard from './components/pages/UserDashboard';
import UserProfile from './components/pages/UserProfile';
import UserProductDetail from './components/pages/UserProductDetail';
import UserEventDetail from './components/pages/UserEventDetail';
import UserNotificationDetail from './components/pages/UserNotificationDetail';
import Layout from './components/Layout/Layout';

// Main Pages
import Users from './components/pages/users/Users';
import UserDetail from './components/pages/users/UserDetail';
import Children from './components/pages/children/Children';
import ChildDetail from './components/pages/children/ChildDetail';
import Notifications from './components/pages/notifications/Notifications';
import NotificationCreate from './components/pages/notifications/NotificationCreate';
import NotificationEdit from './components/pages/notifications/NotificationEdit';
import Services from './components/pages/services/Services';
import ServiceCreate from './components/pages/services/ServiceCreate';
import ServiceDetail from './components/pages/services/ServiceDetail';
import Products from './components/pages/products/Products';
import ProductCreate from './components/pages/products/ProductCreate';
import ProductDetail from './components/pages/products/ProductDetail';
import Orders from './components/pages/orders/Orders';
import OrderDetail from './components/pages/orders/OrderDetail';
import Settings from './components/pages/settings/Settings';
import AppVersions from './components/pages/AppVersions';
import AppDownload from './components/pages/AppDownload';

// Events Pages
import Events from './components/pages/events/Events';
import EventCreate from './components/pages/events/EventCreate';
import EventDetail from './components/pages/events/EventDetail';

// Surveys Pages
import Surveys from './components/pages/surveys/SurveysAdminScreen';
import EditSurvey from './components/pages/surveys/EditSurveyScreen';
import SurveyResults from './components/pages/surveys/SurveyResultsScreen';

// Accounting Pages
import Accounting from './components/pages/accounting/Accounting';

// Legal Pages
import PrivacyPolicy from './components/pages/legal/PrivacyPolicy';
import TermsOfService from './components/pages/legal/TermsOfService';
import LegalNotice from './components/pages/legal/LegalNotice';
import CookiesPolicy from './components/pages/legal/CookiesPolicy';

// Auth
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import EventEdit from './components/pages/events/EventEdit';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height="100vh"
          flexDirection="column"
          gap={2}
        >
          <h2>Algo salió mal</h2>
          <button onClick={() => window.location.reload()}>
            Recargar página
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Smart Redirect Component
function SmartRedirect() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUserType = async () => {
      try {
        const adminData = localStorage.getItem('adminData');
        const userData = localStorage.getItem('userData');

        if (adminData) {
          const adminUserData = JSON.parse(adminData);
          if (adminUserData.userType === 'admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/portal', { replace: true });
          }
        } else if (userData) {
          navigate('/portal', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Error determining redirect:', error);
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkUserType();
  }, [navigate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return null;
}

// Auth Guard Component
function AuthGuard({ children, requireAdmin = false }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (isMounted) {
        // Resetear estado al cambiar de usuario para evitar acceso stale
        setHasAccess(false);
        setLoading(true);
        setUser(currentUser);

        if (!currentUser) {
          setLoading(false);
          navigate('/login', { replace: true });
          return;
        }

        // Si requiere admin, verificar el rol desde Firestore
        if (requireAdmin) {
          try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();

              if (userData.userType === 'admin' || userData.role === 'admin') {
                setHasAccess(true);
              } else {
                // Mantener loading=true para evitar flash de pantalla blanca
                navigate('/portal', { replace: true });
                return;
              }
            } else {
              navigate('/login', { replace: true });
              return;
            }
          } catch (error) {
            console.error('Error al verificar acceso admin:', error);
            navigate('/login', { replace: true });
            return;
          }
        } else {
          // Para rutas no-admin, solo verificar que esté autenticado
          setHasAccess(true);
        }

        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [navigate, requireAdmin]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !hasAccess) {
    return null;
  }

  return children;
}

// Loading Component
function LoadingFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
      <CircularProgress size={24} />
    </Box>
  );
}

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2196F3',
    },
    secondary: {
      main: '#4CAF50',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif'
    ].join(','),
  },
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (isMounted) {
        setUser(currentUser);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <Router>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Auth Routes */}
                <Route
                  path="/login"
                  element={user ? <Navigate to="/" replace /> : <Login />}
                />
                <Route
                  path="/register"
                  element={user ? <Navigate to="/" replace /> : <Register />}
                />
                <Route
                  path="/forgot-password"
                  element={user ? <Navigate to="/" replace /> : <ForgotPassword />}
                />

                {/* Legal Routes - Publicly accessible */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/legal-notice" element={<LegalNotice />} />
                <Route path="/cookies-policy" element={<CookiesPolicy />} />
                
                {/* Portal de Usuario */}
                <Route
                  path="/portal"
                  element={
                    <AuthGuard>
                      <AppDownload />
                    </AuthGuard>
                  }
                />

                {/* Perfil de Usuario */}
                <Route
                  path="/portal/perfil"
                  element={
                    <AuthGuard>
                      <UserProfile />
                    </AuthGuard>
                  }
                />

                {/* Detalle de Producto para Usuario */}
                <Route
                  path="/portal/producto/:productId"
                  element={
                    <AuthGuard>
                      <UserProductDetail />
                    </AuthGuard>
                  }
                />

                {/* Detalle de Evento para Usuario */}
                <Route
                  path="/portal/evento/:eventId"
                  element={
                    <AuthGuard>
                      <UserEventDetail />
                    </AuthGuard>
                  }
                />

                {/* Detalle de Notificación para Usuario */}
                <Route
                  path="/portal/notificacion/:notificationId"
                  element={
                    <AuthGuard>
                      <UserNotificationDetail />
                    </AuthGuard>
                  }
                />

                {/* Rutas de Administración */}
                <Route
                  path="/admin"
                  element={
                    <AuthGuard requireAdmin={true}>
                      <Layout />
                    </AuthGuard>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route path="users" element={<Users />} />
                  <Route path="users/:userId" element={<UserDetail />} />
                  <Route path="children" element={<Children />} />
                  <Route path="children/:childId" element={<ChildDetail />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="notifications/create" element={<NotificationCreate />} />
                  <Route path="notifications/:notificationId" element={<NotificationEdit />} />

                  {/* Rutas de Eventos - ACTUALIZADAS */}
                  <Route path="events" element={<Events />} />
                  <Route path="events/new" element={<EventCreate />} />
                  <Route path="events/:eventId" element={<EventDetail />} />
                  <Route path="events/:eventId/edit" element={<EventEdit />} />

                  {/* Rutas de Encuestas */}
                  <Route path="surveys" element={<Surveys />} />
                  <Route path="surveys/create" element={<EditSurvey />} />
                  <Route path="surveys/edit/:surveyId" element={<EditSurvey />} />
                  <Route path="surveys/results/:surveyId" element={<SurveyResults />} />

                  <Route path="services" element={<Services />} />
                  <Route path="services/create" element={<ServiceCreate />} />
                  <Route path="services/:serviceId" element={<ServiceDetail />} />
                  <Route path="products" element={<Products />} />
                  <Route path="products/create" element={<ProductCreate />} />
                  <Route path="products/:productId" element={<ProductDetail />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/:orderId" element={<OrderDetail />} />
                  <Route path="accounting" element={<Accounting />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="app-versions" element={<AppVersions />} />
                </Route>

                {/* Ruta raíz - redirige según el tipo de usuario */}
                <Route
                  path="/"
                  element={
                    <SmartRedirect />
                  }
                />
                
                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} /> 
              </Routes>
            </Suspense>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;